import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React from 'react';
import {
    useOrganizerBasicInfo,
    useOrganizerQualityInfo,
} from '../../../../contexts';
import {
    CreatorSignalQualityResponse,
    HypothesisValue1,
} from '../../../RelatedEvents/types/api';
import { useFollowCount } from '../../../_shared/FollowButton/FollowButton';
import { StatsRow } from '../Stats/StatsRow';

import { numberFormatter } from '../../../utils/utils';
import styles from './Info.module.scss';
import { OrganizerLink } from './OrganizerLink';

const MIN_MONTHS_TO_DISPLAY = 5;
const MIN_EVENTS_TO_DISPLAY = 5;
const MIN_FOLLOWERS_TO_DISPLAY = 9;

const getTimeFrameAndNumberOfEvents = (
    creatorSignalData?: CreatorSignalQualityResponse,
): { timeframeInMonths: number; numberOfEvents: number } => {
    if (!creatorSignalData) {
        return { timeframeInMonths: 0, numberOfEvents: 0 };
    }

    const hypothesis1Value = creatorSignalData?.hypothesis[0].value as
        | HypothesisValue1
        | undefined;
    if (hypothesis1Value) {
        const timeframeInMonths = parseInt(hypothesis1Value.timeInMonths, 10);
        const numberOfEvents = parseInt(hypothesis1Value.noOfEvents, 10);
        return { timeframeInMonths, numberOfEvents };
    }

    return { timeframeInMonths: 0, numberOfEvents: 0 };
};

const getTimeframeStatText = (
    timeframeInMonths: number,
): GenericLazyString | string => {
    if (timeframeInMonths < MIN_MONTHS_TO_DISPLAY) {
        return '--';
    }

    if (timeframeInMonths < 12) {
        return gettext('%(months)s months', { months: timeframeInMonths });
    }

    return gettext('%(years)s years', {
        years: Math.floor(timeframeInMonths / 12),
    });
};

const getNumberOfEventsStatText = (
    numberOfEvents: number,
): GenericLazyString | string => {
    if (numberOfEvents < MIN_EVENTS_TO_DISPLAY) {
        return '--';
    }

    return numberFormatter(numberOfEvents);
};

const getFollowersStatText = (
    followersCount: number,
): GenericLazyString | string => {
    if (followersCount < MIN_FOLLOWERS_TO_DISPLAY) {
        return '--';
    }

    return numberFormatter(followersCount);
};

export const Info = () => {
    const { name } = useOrganizerBasicInfo();
    const followersCount = useFollowCount();
    const { creatorSignalData } = useOrganizerQualityInfo();

    const { timeframeInMonths, numberOfEvents } =
        getTimeFrameAndNumberOfEvents(creatorSignalData);

    const stats = [
        {
            label: gettext('Followers'),
            value: getFollowersStatText(followersCount ?? 0),
        },
        {
            label: gettext('Events'),
            value: getNumberOfEventsStatText(numberOfEvents),
        },
        {
            label: gettext('Hosting'),
            value: getTimeframeStatText(timeframeInMonths),
        },
    ];

    return (
        <div className={styles.infoContainer}>
            <OrganizerLink>{name}</OrganizerLink>
            <StatsRow stats={stats} className={styles.statsRow} />
        </div>
    );
};
